import { useCallback, useEffect, useState } from "react";
import { CalendarClock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { EmptyState, ErrorState, Loading, PageHeader, StatusPill } from "@/components/Bits";
import { Field, ghostBtnCls, inputCls, Modal, primaryBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PatientPicker } from "@/components/PatientPicker";
import { useAuth } from "@/context/AuthContext";
import { dmyTime, today } from "@/lib/format";

const blank = {
  patient_id: "",
  doctor_id: "",
  follow_up_date: today(),
  follow_up_time: "10:00",
  reason: "",
  documents_to_bring: "",
  reminder_timing: "1_day_before",
  reminder_time: "10:00",
  custom_reminder_date: "",
  whatsapp_enabled: false,
  whatsapp_consent_confirmed: false,
};

export default function FollowUps() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState(blank);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/hospital/follow-ups");
      setItems(data.items || []);
    } catch (requestError) {
      setError(apiError(requestError));
    }
  }, []);

  useEffect(() => {
    if (!can("hospital.followups.manage")) return;
    load();
    api.get("/hospital/doctors").then(({ data }) => setDoctors(data.items || [])).catch(() => {});
  }, [can, load]);

  if (!can("hospital.followups.manage")) return <NoAccess what="patient follow-ups" />;

  const reset = () => {
    setOpen(false);
    setPicked(null);
    setForm(blank);
    setFormError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    setFormError("");
    if (!picked) {
      setFormError("Search and select an existing patient.");
      return;
    }
    try {
      await api.post("/hospital/follow-ups", { ...form, patient_id: picked.patient_id });
      toast.success("Patient follow-up saved.");
      reset();
      load();
    } catch (requestError) {
      setFormError(apiError(requestError));
    }
  };

  const cancel = async (followUp) => {
    try {
      await api.post(`/hospital/follow-ups/${followUp.follow_up_id}/status`, {
        status: "cancelled",
        reason: "Cancelled from follow-up desk",
      });
      toast.success("Follow-up and any pending reminder were cancelled.");
      load();
    } catch (requestError) {
      toast.error(apiError(requestError));
    }
  };

  return (
    <div data-testid="followups-page">
      <PageHeader
        overline="Appointments"
        title="Patient follow-up / reminder"
        subtitle="Schedule an existing patient’s requested return visit and an optional approved WhatsApp reminder."
        actions={(
          <button className={primaryBtnCls} data-testid="add-followup-button" onClick={() => setOpen(true)}>
            <CalendarClock className="h-4 w-4" /> Add follow-up
          </button>
        )}
      />
      {error && <ErrorState message={error} />}
      {!items ? <Loading label="Loading follow-ups…" /> : (
        <Table
          testid="followups-table"
          columns={["Follow-up", "Patient", "Doctor", "Reason", "Reminder", "Status", "Actions"]}
          rows={items}
          empty={<EmptyState title="No follow-ups" subtitle="Create a structured follow-up from the doctor’s handwritten instruction." testid="followups-empty" />}
          renderRow={(item) => (
            <tr key={item.follow_up_id} data-testid={`followup-row-${item.follow_up_id}`}>
              <Td className="whitespace-nowrap text-slate-700">{dmyTime(item.follow_up_at)}</Td>
              <Td className="font-medium text-slate-900">{item.patient_name}<span className="block text-xs text-slate-400">{item.patient_uhid}</span></Td>
              <Td className="text-slate-600">{item.doctor_name ? `Dr. ${item.doctor_name}` : "—"}</Td>
              <Td className="max-w-[180px] truncate text-slate-600">{item.reason}</Td>
              <Td className="text-slate-600"><StatusPill status={item.reminder_status} /></Td>
              <Td><StatusPill status={item.status} /></Td>
              <Td>{item.status !== "cancelled" && (
                <button className={ghostBtnCls} data-testid={`cancel-followup-${item.follow_up_id}`} onClick={() => cancel(item)}>
                  <XCircle className="h-3.5 w-3.5" /> Cancel
                </button>
              )}</Td>
            </tr>
          )}
        />
      )}
      <Modal open={open} onClose={reset} wide title="Patient follow-up / reminder" testid="followup-modal">
        <div className="mb-6"><PatientPicker selected={picked} onSelect={setPicked} /></div>
        {picked && (
          <form className="grid gap-5 sm:grid-cols-2" onSubmit={submit}>
            <Field label="Doctor"><select className={inputCls} data-testid="followup-doctor-select" value={form.doctor_id} onChange={(event) => setForm({ ...form, doctor_id: event.target.value })}><option value="">Not specified</option>{doctors.map((doctor) => <option key={doctor.doctor_id} value={doctor.doctor_id}>Dr. {doctor.name}</option>)}</select></Field>
            <Field label="Follow-up date *"><input required type="date" className={inputCls} data-testid="followup-date-input" value={form.follow_up_date} onChange={(event) => setForm({ ...form, follow_up_date: event.target.value })} /></Field>
            <Field label="Follow-up time *"><input required type="time" className={inputCls} data-testid="followup-time-input" value={form.follow_up_time} onChange={(event) => setForm({ ...form, follow_up_time: event.target.value })} /></Field>
            <Field label="Reason / follow-up note *"><input required className={inputCls} data-testid="followup-reason-input" value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} /></Field>
            <Field label="Reports / documents to bring" className="sm:col-span-2"><input className={inputCls} data-testid="followup-documents-input" value={form.documents_to_bring} onChange={(event) => setForm({ ...form, documents_to_bring: event.target.value })} /></Field>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="followup-whatsapp-enabled" checked={form.whatsapp_enabled} onChange={(event) => setForm({ ...form, whatsapp_enabled: event.target.checked })} /> Send WhatsApp reminder using this workspace’s approved template</label>
            {form.whatsapp_enabled && <>
              <Field label="Reminder timing"><select className={inputCls} data-testid="followup-reminder-timing" value={form.reminder_timing} onChange={(event) => setForm({ ...form, reminder_timing: event.target.value })}><option value="same_day">Same day</option><option value="1_day_before">1 day before</option><option value="2_days_before">2 days before</option><option value="3_days_before">3 days before</option><option value="custom">Custom</option></select></Field>
              <Field label="Reminder time"><input type="time" className={inputCls} data-testid="followup-reminder-time" value={form.reminder_time} onChange={(event) => setForm({ ...form, reminder_time: event.target.value })} /></Field>
              {form.reminder_timing === "custom" && <Field label="Custom reminder date"><input required type="date" className={inputCls} data-testid="followup-custom-reminder-date" value={form.custom_reminder_date} onChange={(event) => setForm({ ...form, custom_reminder_date: event.target.value })} /></Field>}
              <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700"><input required type="checkbox" data-testid="followup-consent-confirmed" checked={form.whatsapp_consent_confirmed} onChange={(event) => setForm({ ...form, whatsapp_consent_confirmed: event.target.checked })} /> Patient has consented to this WhatsApp reminder.</label>
            </>}
            {formError && <p className="sm:col-span-2 text-sm text-red-600" data-testid="followup-form-error">{formError}</p>}
            <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="save-followup-button">Save follow-up</button></div>
          </form>
        )}
      </Modal>
    </div>
  );
}